import { Card, CardContent } from '@/shared/ui/card';
import { ChevronLeft, ChevronRight, Notebook, Camera, Tag } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { useState, useEffect } from 'react';
import { useHotspotObjects } from './hooks/hooks';
import { getObjectTypePrimaryColor, getObjectTypeBackground } from './utils/hotspotUtils';

interface HotspotData {
  id: string;
  type: string;
  pitch: number;
  yaw: number;
  originalId: number;
  title?: string;
}

interface HotspotLegendProps {
  hotspots?: HotspotData[];
  onNavigateToHotspot?: (pitch: number, yaw: number) => void;
  onHotspotClick?: (hotspot: HotspotData) => void;
  projectId?: string;
  // Navigation controls props
  photoName?: string;
  photoDate?: string;
  currentPhotoIndex?: number;
  totalPhotos?: number;
  hasPhotoPrev?: boolean;
  hasPhotoNext?: boolean;
  onNavigatePhoto?: (direction: 'prev' | 'next') => void;
}

const HotspotLegend: React.FC<HotspotLegendProps> = ({
  hotspots = [],
  onNavigateToHotspot,
  onHotspotClick,
  projectId,
  photoName,
  photoDate,
  currentPhotoIndex,
  totalPhotos,
  hasPhotoPrev,
  hasPhotoNext,
  onNavigatePhoto,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentHotspot = hotspots[currentIndex];
  const { objects: hotspotObjects } = useHotspotObjects(currentHotspot?.originalId, projectId);

  const objectTypes = [
    {
      type: 'note',
      icon: Notebook,
      label: 'Notes',
      color: getObjectTypePrimaryColor('note'),
      bgColor: getObjectTypeBackground('note'),
    },
    {
      type: 'image',
      icon: Camera,
      label: 'Photos',
      color: getObjectTypePrimaryColor('image'),
      bgColor: getObjectTypeBackground('image'),
    },
    {
      type: 'label',
      icon: Tag,
      label: 'Labels',
      color: getObjectTypePrimaryColor('label'),
      bgColor: getObjectTypeBackground('label'),
    },
  ];

  const getObjectCount = (type: string) => {
    return hotspotObjects?.filter((obj) => obj.type === type).length || 0;
  };

  useEffect(() => {
    setCurrentIndex(0);
  }, [hotspots]);

  const navigateToPrevious = () => {
    if (hotspots.length === 0) return;

    const newIndex = currentIndex > 0 ? currentIndex - 1 : hotspots.length - 1;
    setCurrentIndex(newIndex);

    const hotspot = hotspots[newIndex];
    if (onNavigateToHotspot && hotspot) {
      onNavigateToHotspot(hotspot.pitch, hotspot.yaw);
    }
  };

  const navigateToNext = () => {
    if (hotspots.length === 0) return;

    const newIndex = currentIndex < hotspots.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);

    const hotspot = hotspots[newIndex];
    if (onNavigateToHotspot && hotspot) {
      onNavigateToHotspot(hotspot.pitch, hotspot.yaw);
    }
  };

  const handlePositionClick = () => {
    const hotspot = hotspots[currentIndex];

    if (hotspot && onHotspotClick) {
      onHotspotClick(hotspot);
    }
  };

  const totalCount = hotspots.length;

  return (
    <Card
      className="absolute z-10 bg-gray-500 border-2 border-white
                 bottom-4 left-4 right-4 md:bottom-auto md:left-auto md:right-4 md:top-16 md:w-[145px]"
      data-testid="hotspot-legend"
    >
      <CardContent className="p-3 rounded-lg" data-testid="hotspot-legend-content">
        {/* Mobile Horizontal Layout */}
        <div className="flex md:hidden">
          {/* Left Section: Navigation and Title */}
          <div className="flex-1">
            {/* Top half: Navigation */}
            <div className="flex items-center justify-center space-x-2 mb-2">
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-white"
                style={{
                  backgroundColor: 'rgba(0, 13, 66, 0.7)',
                  boxShadow: '0 0 10px 6px rgba(0, 0, 0, 0.2)',
                }}
              />

              {totalCount > 0 && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 text-white hover:bg-white/20"
                    onClick={navigateToPrevious}
                    disabled={totalCount === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  <button
                    className="text-white text-xs min-w-[35px] text-center hover:bg-white/20 rounded px-1 py-0.5 transition-colors cursor-pointer"
                    onClick={handlePositionClick}
                  >
                    {totalCount > 0 ? `${currentIndex + 1}/${totalCount}` : '-'}
                  </button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 text-white hover:bg-white/20"
                    onClick={navigateToNext}
                    disabled={totalCount === 0}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </>
              )}
              {totalCount === 0 && (
                <span className="text-white text-sm font-medium">{totalCount}</span>
              )}
            </div>

            {/* Bottom half: Hotspot Title */}
            {currentHotspot && (
              <button
                className="text-white text-sm font-medium text-center w-full hover:bg-white/10 rounded px-2 py-1 transition-colors cursor-pointer break-words leading-tight"
                onClick={() => {
                  if (currentHotspot && onHotspotClick) {
                    onHotspotClick(currentHotspot);
                  }
                }}
                title={`Open ${currentHotspot.title || 'No Title'}`}
              >
                {currentHotspot.title || 'No Title'}
              </button>
            )}
          </div>

          {/* Divider */}
          {currentHotspot && <div className="mx-3 border-l border-white/20"></div>}

          {/* Right Section: Object Counts */}
          {currentHotspot && (
            <div className="flex items-center space-x-3">
              {objectTypes.map((objType) => {
                const count = getObjectCount(objType.type);
                const IconComponent = objType.icon;

                return (
                  <div key={objType.type} className="flex flex-col items-center space-y-1">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: objType.color }}
                    >
                      <IconComponent className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-white text-xs font-medium">{count}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Right Section: Navigation Controls on Mobile */}
          {photoName && (
            <>
              <div className="mx-2 border-l border-white/20"></div>
              <div className="flex flex-col items-center space-y-1 min-w-0 flex-shrink">
                <div className="text-white text-[10px] font-medium text-center truncate max-w-[80px]">
                  {photoName.replace('.jpg', '').replace('.jpeg', '').replace('.png', '')}
                </div>
                <div className="text-white text-[9px] text-center">{photoDate}</div>
                {totalPhotos && totalPhotos > 1 && (
                  <div className="flex items-center gap-1">
                    {hasPhotoPrev && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 text-white hover:bg-white/20"
                        onClick={() => onNavigatePhoto?.('prev')}
                      >
                        <ChevronLeft className="h-3 w-3" />
                      </Button>
                    )}
                    <span className="text-white text-[9px] min-w-[20px] text-center">
                      {(currentPhotoIndex || 0) + 1}/{totalPhotos}
                    </span>
                    {hasPhotoNext && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 text-white hover:bg-white/20"
                        onClick={() => onNavigatePhoto?.('next')}
                      >
                        <ChevronRight className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Desktop Vertical Layout */}
        <div className="hidden md:block">
          <div className="flex items-center space-x-2" data-testid="hotspot-legend-item">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-white"
              style={{
                backgroundColor: 'rgba(0, 13, 66, 0.7)',
                boxShadow: '0 0 10px 6px rgba(0, 0, 0, 0.2)',
              }}
            />

            <div className="flex items-center space-x-1 flex-1">
              {totalCount > 0 && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 text-white hover:bg-white/20"
                    onClick={navigateToPrevious}
                    disabled={totalCount === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  <button
                    className="text-white text-xs min-w-[35px] text-center hover:bg-white/20 rounded px-1 py-0.5 transition-colors cursor-pointer"
                    onClick={handlePositionClick}
                  >
                    {totalCount > 0 ? `${currentIndex + 1}/${totalCount}` : '-'}
                  </button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 text-white hover:bg-white/20"
                    onClick={navigateToNext}
                    disabled={totalCount === 0}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </>
              )}
              {totalCount === 0 && (
                <span className="text-white text-sm font-medium min-w-[20px] text-center">
                  {totalCount}
                </span>
              )}
            </div>
          </div>

          {/* Hotspot Title */}
          {currentHotspot && (
            <div className="mt-3 border-t border-white/20 pt-3">
              <button
                className="text-white text-sm font-medium text-center mb-2 w-full hover:bg-white/10 rounded px-2 py-1 transition-colors cursor-pointer break-words leading-tight"
                onClick={() => {
                  if (currentHotspot && onHotspotClick) {
                    onHotspotClick(currentHotspot);
                  }
                }}
                title={`Open ${currentHotspot.title || 'No Title'}`}
              >
                {currentHotspot.title || 'No Title'}
              </button>

              {/* Object Counts */}
              <div className="space-y-1">
                {objectTypes.map((objType) => {
                  const count = getObjectCount(objType.type);
                  const IconComponent = objType.icon;

                  return (
                    <div key={objType.type} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: objType.color }}
                        >
                          <IconComponent className="w-2.5 h-2.5 text-white" />
                        </div>
                        <span className="text-white text-xs">{objType.label}</span>
                      </div>
                      <span className="text-white text-xs font-medium min-w-[16px] text-right">
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Photo Information for Desktop - Always at bottom */}
          {photoName && (
            <div className="mt-2 border-t border-white/20 pt-2">
              <div className="text-white text-[10px] font-medium mb-1 truncate">
                {photoName.replace('.jpg', '').replace('.jpeg', '').replace('.png', '')}
              </div>
              <div className="text-white text-[9px] mb-1">{photoDate}</div>
              {totalPhotos && totalPhotos > 1 && (
                <div className="flex items-center gap-1">
                  {hasPhotoPrev && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 text-white hover:bg-white/20"
                      onClick={() => onNavigatePhoto?.('prev')}
                    >
                      <ChevronLeft className="h-3 w-3" />
                    </Button>
                  )}
                  <span className="text-white text-[9px] min-w-[20px] text-center">
                    {(currentPhotoIndex || 0) + 1}/{totalPhotos}
                  </span>
                  {hasPhotoNext && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 text-white hover:bg-white/20"
                      onClick={() => onNavigatePhoto?.('next')}
                    >
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default HotspotLegend;
